import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

const THREAD_READ_EVENT = "coordinator-thread-read";

export function notifyThreadRead() {
  window.dispatchEvent(new Event(THREAD_READ_EVENT));
}

export function useCoordinatorUnreadCount() {
  const { user } = useAuth();
  const [count, setCount] = useState(0);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refresh = useCallback(async () => {
    if (!user?.id) { setCount(0); return; }
    const { data, error } = await supabase.rpc("coordinator_unread_count" as never);
    if (!error && typeof data === "number") setCount(data);
  }, [user?.id]);

  useEffect(() => { void refresh(); }, [refresh]);

  useEffect(() => {
    const handler = () => void refresh();
    window.addEventListener(THREAD_READ_EVENT, handler);
    return () => window.removeEventListener(THREAD_READ_EVENT, handler);
  }, [refresh]);

  useEffect(() => {
    if (!user?.id) return;
    const debouncedRefresh = () => {
      if (debounce.current) clearTimeout(debounce.current);
      debounce.current = setTimeout(() => void refresh(), 400);
    };
    const channel = supabase
      .channel("coord-unread")
      .on("postgres_changes" as never, { event: "INSERT", schema: "public", table: "coordinator_messages" }, debouncedRefresh)
      .on("postgres_changes" as never, { event: "UPDATE", schema: "public", table: "coordinator_threads" }, debouncedRefresh)
      .subscribe();
    return () => {
      if (debounce.current) clearTimeout(debounce.current);
      void supabase.removeChannel(channel);
    };
  }, [user?.id, refresh]);

  return count;
}

type UnreadPerAgent = { counterpart_user_id: string; counterpart_name: string; unread_count: number };

export function useCoordinatorUnreadPerAgent() {
  const { user } = useAuth();
  const [items, setItems] = useState<UnreadPerAgent[]>([]);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refresh = useCallback(async () => {
    if (!user?.id) { setItems([]); return; }
    const { data, error } = await supabase.rpc("coordinator_unread_per_thread" as never);
    if (!error && Array.isArray(data)) setItems(data as UnreadPerAgent[]);
  }, [user?.id]);

  useEffect(() => { void refresh(); }, [refresh]);

  useEffect(() => {
    const handler = () => void refresh();
    window.addEventListener(THREAD_READ_EVENT, handler);
    return () => window.removeEventListener(THREAD_READ_EVENT, handler);
  }, [refresh]);

  useEffect(() => {
    if (!user?.id) return;
    const debouncedRefresh = () => {
      if (debounce.current) clearTimeout(debounce.current);
      debounce.current = setTimeout(() => void refresh(), 400);
    };
    const channel = supabase
      .channel("coord-unread-detail")
      .on("postgres_changes" as never, { event: "INSERT", schema: "public", table: "coordinator_messages" }, debouncedRefresh)
      .on("postgres_changes" as never, { event: "UPDATE", schema: "public", table: "coordinator_threads" }, debouncedRefresh)
      .subscribe();
    return () => {
      if (debounce.current) clearTimeout(debounce.current);
      void supabase.removeChannel(channel);
    };
  }, [user?.id, refresh]);

  const byUser = new Map<string, number>();
  for (const item of items) byUser.set(item.counterpart_user_id, item.unread_count);

  return { items, unreadFor: (userId: string) => byUser.get(userId) ?? 0 };
}
